import React, { useEffect, useState } from "react";
import {
  Dialog,
  TextField,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

const SubscriptionPlanModel = ({ open, onClose, onSubmit, editData }) => {
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    features: [],
    prices: { monthly: "", quarterly: "", yearly: "" },
  });

  const [featureName, setFeatureName] = useState("");
  const [featureIncluded, setFeatureIncluded] = useState(true);
  const [featureTextValue, setFeatureTextValue] = useState("");

  useEffect(() => {
    if (editData) {
      setFormData(editData);
    } else {
      setFormData({
        id: "",
        name: "",
        features: [],
        prices: { monthly: "", quarterly: "", yearly: "" },
      });
    }
  }, [editData, open]);

  const handleAddFeature = () => {
    if (!featureName.trim()) return;

    setFormData((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        {
          name: featureName,
          included: Boolean(featureIncluded),
          textValue: featureTextValue.trim(),
        },
      ],
    }));

    setFeatureName("");
    setFeatureIncluded(true);
    setFeatureTextValue("");
  };

  const handleRemoveFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 10, right: 10 }}>
        <CloseIcon />
      </IconButton>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          {editData ? "Edit Plan" : "Add Subscription Plan"}
        </Typography>

        <TextField
          label="Plan Name (Basic / Standard / Premium)"
          value={formData.name}
          required
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />

        {["monthly", "quarterly", "yearly"].map((type) => (
          <FormControl key={type} fullWidth required>
            <InputLabel>{type.toUpperCase()} Price</InputLabel>
            <OutlinedInput
              type="number"
              value={formData.prices[type]}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  prices: { ...formData.prices, [type]: e.target.value },
                })
              }
              startAdornment={<InputAdornment position="start">₦</InputAdornment>}
              label={`${type} Price`}
            />
          </FormControl>
        ))}

        <Typography variant="subtitle1" fontWeight="bold">
          Features
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Feature Name"
            sx={{ flex: 2 }}
            value={featureName}
            onChange={(e) => setFeatureName(e.target.value)}
          />

          <FormControl sx={{ minWidth: 100 }}>
            <InputLabel>Included</InputLabel>
            <Select
              value={featureIncluded}
              label="Included"
              onChange={(e) => setFeatureIncluded(e.target.value === "true")}
            >
              <MenuItem value={true}>Yes</MenuItem>
              <MenuItem value={false}>No</MenuItem>
            </Select>
          </FormControl>

          {featureIncluded && (
            <TextField
              label="Limit / Text (Optional)"
              sx={{ flex: 1.5 }}
              placeholder="e.g. Unlimited"
              value={featureTextValue}
              onChange={(e) => setFeatureTextValue(e.target.value)}
            />
          )}

          <button
            type="button"
            onClick={handleAddFeature}
            style={{
              padding: "8px 16px",
              border: "1px solid #1976d2",
              background: "transparent",
              color: "#1976d2",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Add
          </button>
        </Box>

        <Box>
          {formData.features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                justifyContent: "space-between",
                padding: "8px",
                border: "1px solid #eee",
                borderRadius: "4px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {feature.included ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <CancelIcon color="error" fontSize="small" />
                )}
                <Typography variant="body2">{feature.name}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {feature.included && feature.textValue && (
                  <Typography variant="body2" color="textSecondary">
                    {feature.textValue}
                  </Typography>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveFeature(index)}
                  style={{
                    padding: "2px 8px",
                    border: "none",
                    background: "#f44336",
                    color: "#fff",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Remove
                </button>
              </Box>
            </Box>
          ))}
        </Box>

        <button
          type="submit"
          style={{
            padding: "10px",
            background: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          SAVE
        </button>
      </Box>
    </Dialog>
  );
};

export default React.memo(SubscriptionPlanModel);
